import { ref } from 'vue'
import { getStorage, ref as storageRef, getDownloadURL } from 'firebase/storage'
import { getApp } from 'firebase/app'
import { handleErrors } from '../utils/errorHandler'
import { useAuthStore } from '../stores/AuthStore'

/**
 * Resolves the current user's agency branding (agencyId, logoSize, logoPath,
 * whitelabelColor) from the cached user profile + Firebase Storage. Used by
 * Content Library to drive the white-label download modal and to pass branding
 * context to the local branding engines.
 *
 * State is module-scoped so every consumer shares the same refs and the
 * underlying profile request is performed at most once per authenticated
 * identity (via AuthStore.fetchUserProfile).
 *
 * Call `resetAgencyBranding()` during logout otherwise a new session in the same
 * tab keeps the previous user's refs and skips refetch (`brandingLoaded`).
 */
const agencyId = ref(null)
const logoSize = ref('')
const logoPath = ref('')
const whitelabelColor = ref('')
const brandingLoaded = ref(false)
let inFlightLoad = null
// Bump on reset so completions from stale in-flight loads never repopulate refs.
let brandingEpoch = 0

export function resetAgencyBranding() {
  brandingEpoch++
  agencyId.value = null
  logoSize.value = ''
  logoPath.value = ''
  whitelabelColor.value = ''
  brandingLoaded.value = false
  inFlightLoad = null
}

export function useAgencyBranding() {
  const authStore = useAuthStore()

  async function loadBranding() {
    if (brandingLoaded.value) return
    if (inFlightLoad) return inFlightLoad

    inFlightLoad = (async () => {
      const epochWhenStarted = brandingEpoch

      try {
        const data = await authStore.fetchUserProfile()
        if (epochWhenStarted !== brandingEpoch) return
        if (!data) return

        agencyId.value = data.agencyId || null
        logoSize.value = data.logoSize || ''
        whitelabelColor.value = data.whitelabelColor || ''

        if (agencyId.value && logoSize.value) {
          const filename = `${agencyId.value}_${logoSize.value}.png`
          try {
            const storage = getStorage(getApp())
            const fileRef = storageRef(storage, `agencies-logos/${filename}`)
            const downloadUrl = await getDownloadURL(fileRef)
            if (epochWhenStarted !== brandingEpoch) return
            logoPath.value = downloadUrl
          } catch (logoErr) {
            handleErrors('useAgencyBranding.loadBranding.logo', logoErr)
          }
        }

        if (epochWhenStarted !== brandingEpoch) return
        brandingLoaded.value = true
      } catch (_err) {
        // Branding is optional — failures should not block the flow
      } finally {
        inFlightLoad = null
      }
    })()

    return inFlightLoad
  }

  return {
    agencyId,
    logoSize,
    logoPath,
    whitelabelColor,
    brandingLoaded,
    loadBranding,
    resetAgencyBranding
  }
}
